<template>
  <div class="mini-calendar">
    <!-- Toggle View Buttons (Only Show If "both" is Selected) -->
    <div
      class="mini-calendar__view-toggle-wrap"
      v-if="calendarViewMode === 'both'"
    >
      <button
        class="mini-calendar__view-toggle"
        @click="toggleView('list')"
        :aria-current="!isMonthView ? 'true' : undefined"
      >
        List
      </button>
      <button
        class="mini-calendar__view-toggle"
        @click="toggleView('month')"
        :aria-current="isMonthView ? 'true' : undefined"
      >
        Month
      </button>
    </div>
    <!-- Search & Filter -->
    <div class="mini-calendar__search-filters">
      <MiniCalenderSearch />
      <button
        data-dialog="mini-calendar-filter-modal"
        class="mini-calendar__filter-toggle-button"
      >
        <svg aria-hidden="true" width="25" height="23">
          <use xlink:href="#icon-filter"></use>
        </svg>
        <span class="mini-calendar__filter-toggle-button-label">
          Filter Events
        </span>
      </button>
    </div>

    <MiniCalenderPool
      :isMonthView="isMonthView"
      @resetall-month-view="resetAll"
      @reset-month-view="resetMonthToToday"
    />
    <!-- Month or List View -->
    <div v-if="calendarViewMode === 'both'">
      <div v-if="isMonthView">
        <div v-if="!isMobile">
          <MiniCalendarMonthView
            :key="monthViewKey"
            :show-date="date_range.start"
            @update:noEvents="noEventsInMonth = $event"
            ref="MiniCalendarMonthView"
          />
          <div class="mini-calendar__events-no-result" v-if="noEventsInMonth">
            <p class="mini-calendar__events-no-result-text">
              <template
                v-if="
                  calendarViewMode === 'month' ||
                  (calendarViewMode === 'both' && isMonthView)
                "
              >
                <template v-if="hasActiveFilters">
                  Sorry, no matching events were found for this month.
                  <a
                    href="javascript:void(0);"
                    @click="showAllEvents"
                    class="mini-calendar__events-no-result-link"
                    >Start over</a
                  >, or go to a different month.
                </template>
                <template v-else>
                  Sorry, no upcoming events for this month.
                </template>
              </template>

              <template v-else>
                <template v-if="hasActiveFilters">
                  Sorry, no matching events were found.
                  <a
                    href="javascript:void(0);"
                    @click="showAllEvents"
                    class="mini-calendar__events-no-result-link"
                    >Show all events</a
                  >.
                </template>
                <template v-else>
                  Sorry, no upcoming events this month.
                </template>
              </template>
            </p>
          </div>
        </div>
        <div v-else class="mini-calendar__mobile-message">
          <p>
            Please resize your browser or reload the page to see all upcoming
            events.
          </p>
        </div>
      </div>
      <div v-else>
        <MiniCalenderEvents
          :isMonthView="isMonthView"
          :hasFilters="hasActiveFilters"
        />
      </div>
    </div>

    <div v-else-if="calendarViewMode === 'month'">
      <MiniCalendarMonthView
        :key="monthViewKey"
        :show-date="date_range.start"
        @update:noEvents="noEventsInMonth = $event"
        ref="MiniCalendarMonthView"
      />
      <div class="mini-calendar__events-no-result" v-if="noEventsInMonth">
        <p class="mini-calendar__events-no-result-text">
          <template
            v-if="
              calendarViewMode === 'month' ||
              (calendarViewMode === 'both' && isMonthView)
            "
          >
            <template v-if="hasActiveFilters">
              Sorry, no matching events were found for this month.
              <a
                href="javascript:void(0);"
                @click="showAllEvents"
                class="mini-calendar__events-no-result-link"
                >Start over</a
              >, or go to a different month.
            </template>
            <template v-else>
              Sorry, no upcoming events for this month.
            </template>
          </template>

          <template v-else>
            <template v-if="hasActiveFilters">
              Sorry, no matching events were found.
              <a
                href="javascript:void(0);"
                @click="showAllEvents"
                class="mini-calendar__events-no-result-link"
                >Show all events</a
              >.
            </template>
            <template v-else> Sorry, no upcoming events this month. </template>
          </template>
        </p>
      </div>
    </div>

    <div v-else-if="calendarViewMode === 'list'">
      <MiniCalenderEvents
        :isMonthView="isMonthView"
        :hasFilters="hasActiveFilters"
      />
    </div>
    <!-- Filters Modal -->
    <MiniCalendarFilterModal
      @month-changed="refreshMonthView"
      @close="closeModal"
    />
  </div>
</template>

<script>
import MiniCalenderEvents from "./mini-calendar-events.vue";
import MiniCalenderSearch from "./mini-calendar-search.vue";
import MiniCalenderPool from "./mini-calendar-pool.vue";
import { getEvents, getCategories, getFilteredEvents } from "./../api";
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import { generateStaticDay } from "./../utils/util";
import { DATE_FORMAT } from "./../utils/config";
import MiniCalendarFilterModal from "./mini-calendar-filtermodal.vue";
import MiniCalendarMonthView from "./mini-calendar-month.vue";

export default {
  name: "HomePage",
  components: {
    MiniCalenderEvents,
    MiniCalenderSearch,
    MiniCalenderPool,
    MiniCalendarFilterModal,
    MiniCalendarMonthView,
  },
  created() {
    const param = this.$route.query;
    this.LoadEvents(param);
    // Handle prefiltering from URL
    Object.keys(param).forEach((key) => {
      if (key.startsWith("category_") && param[key]) {
        const terms = Array.isArray(param[key]) ? param[key] : [param[key]];
        this.$store.dispatch("updateSelectedCategories", {
          key: `${key}[]`,
          terms,
        });
        this.$store.commit("OPEN_FILTER", key);
      }
    });
  },
  watch: {
    openFilters(newVal) {
      if (newVal && newVal[this.categoryId]) {
        this.isHidden = false;
      }
    },
  },
  data() {
    return {
      isMobile: false,
      mobileMQ: null,
      isMonthView: false,
      noEventsInMonth: false,
      monthViewKey: 0,
      calendarViewMode:
        window.drupalSettings?.idfive_calendar_client?.options
          ?.calendar_view_mode || "both", // Ensure safe retrieval
    };
  },
  computed: {
    ...mapGetters(["eventlist", "date_range", "total"]),
    hasActiveFilters() {
      const filters = this.$store.state.selectedCategories || {};
      const hasCategoryFilters = Object.keys(filters).some(
        (key) => filters[key].length > 0
      );
      const hasSearchTerm = this.$store.getters.search?.trim()?.length > 0;
      return hasCategoryFilters || hasSearchTerm;
    },
  },
  methods: {
    resetAll() {
      //this applies to only month view
      this.setLoading(true);
      this.setClearAll();
      this.setFilter([]);
      this.$store.state.selectedCategories = {};
    },
    resetMonthToToday() {
      const today = moment().format("YYYY-MM");
      this.setDateManuallySelected(false);
      this.setSelectedMonth(today);
      // resetall on month view to get current month events
      this.monthViewKey += 1;
    },
    showAllEvents() {
      this.resetMonthToToday();
      this.setLoading(true);
      this.setClearAll();
      this.setFilter([]);
      this.$store.state.selectedCategories = {};
    },

    refreshMonthView(newMonth) {
      this.setSelectedMonth(
        moment(newMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
      );
      // Trigger the month toggle button if the current view is month
      if (this.isMonthView) {
        this.isMonthView = false; // Temporarily switch to list view
        this.$nextTick(() => {
          this.isMonthView = true; // Switch back to month view
        });
      }
    },
    toggleView(view) {
      if (this.calendarViewMode === "both") {
        this.isMonthView = view === "month";
      }
      if (this.isMonthView) {
        this.$store.state.isMonthView = true;
      } else {
        this.$store.state.isMonthView = false;
      }
      // Use the selected month from Vuex when toggling views
      const selectedMonth =
        this.$store.state.selectedMonth || moment().format("YYYY-MM");
      this.setSelectedMonth(selectedMonth);
      const startOfMonth = moment(selectedMonth, "YYYY-MM").format(
        "YYYY-MM-DD"
      );
      const endOfMonth = moment(selectedMonth, "YYYY-MM")
        .endOf("month")
        .format("YYYY-MM-DD");
      this.$store.dispatch("setDateRange", {
        type: "custom_range",
        start: startOfMonth,
        end: endOfMonth,
      });
      // Load events for the selected month when switching from list to month
      //const currentMonth = moment().format("YYYY-MM");
      const searchTerm = this.$store.getters.search || "";
      const queryString = Object.entries(this.$store.state.selectedCategories)
        .flatMap(([key, terms]) => terms.map((term) => `${key}=${term}`))
        .join("&");
      if (!this.isMonthView) {
        this.getLoadFilteredEvents(
          moment(startOfMonth),
          moment(endOfMonth),
          queryString,
          0,
          searchTerm
        );
      }
    },
    resize(event) {
      this.isMobile = event.matches;
    },
    CheckFilters(categorylist, filters) {
      let filtered_ids = [];
      if (categorylist.keys.length) {
        for (let key of categorylist.keys) {
          let filtered = categorylist.data[key].terms.filter(
            (item) => filters.indexOf(item.id) >= 0
          );
          if (filtered.length) {
            for (let term of filtered) {
              filtered_ids.push(term.id);
            }
          }
        }
      }

      return filtered_ids;
    },
    LoadEvents(param) {
      this.setLoading(true);
      let filters = [];
      const searchTerm = this.$store.getters.search || "";
      if (param && Object.keys(param).length) {
        Object.entries(param).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            filters.push(...value);
          } else {
            filters.push(value);
          }
          // Update Vuex state with prefiltering values
          this.$store.dispatch("updateSelectedCategories", {
            key: `${key}[]`, // Ensure key includes `[]`
            terms: Array.isArray(value) ? value : [value],
          });
        });
      }
      const selectedCategoriesQueryString = Object.entries(
        this.$store.state.selectedCategories || {}
      )
        .flatMap(([key, terms]) => terms.map((term) => `${key}=${term}`))
        .join("&");
      const urlQueryString = Object.entries(param || {})
        .flatMap(([key, value]) =>
          Array.isArray(value)
            ? value.map((val) => `${key}[]=${val}`)
            : [`${key}[]=${value}`]
        )
        .join("&");

      const finalQueryString =
        filters.length > 0 ? urlQueryString : selectedCategoriesQueryString;
      if (!filters.length) {
        getEvents()
          .then((eventlist) => {
            getCategories()
              .then((categorylist) => {
                this.setEventList({
                  eventlist: eventlist.data,
                  total: eventlist.query.total,
                });
                this.setCategoryList(categorylist);
                this.setLoading(false);
              })
              .catch(() => {
                this.setLoading(false);
              });
          })
          .catch(() => {
            this.setLoading(false);
          });
      } else {
        const startDate =
          this.date_range.start || moment().format("YYYY-MM-DD");
        //const startDate =  moment(this.selectedMonth, "YYYY-MM-DD")
        const endDate = this.date_range.end || "";
        getFilteredEvents(startDate, endDate, finalQueryString, 0, searchTerm)
          .then((eventlist) => {
            getCategories()
              .then(async (categorylist) => {
                this.setEventList({
                  eventlist: eventlist.data,
                  total: eventlist.query.total,
                });
                await this.setCategoryList(categorylist);
                const filter = this.CheckFilters(categorylist, filters);
                if (filter.length) {
                  this.setFilter(filter);
                }

                this.setLoading(false);
              })
              .catch(() => {
                this.setLoading(false);
              });
          })
          .catch(() => {
            this.setLoading(false);
          });
      }
      // const start = moment().startOf("month"),
      //   end = moment().endOf("month");
      const start = moment(this.selectedMonth).startOf("month");
      const end = moment(this.selectedMonth).endOf("month");
      getFilteredEvents(
        start.format(DATE_FORMAT),
        end.format(DATE_FORMAT),
        finalQueryString,
        0,
        searchTerm
      ).then((eventlist) => {
        let monthevents = [];
        for (let event of eventlist.data) {
          if (moment(event.date.start.date) > moment().endOf("month")) {
            break;
          }

          monthevents.push(moment(event.date.start.date).toDate());
        }

        this.setMonthEvents([
          generateStaticDay(monthevents, start.format(DATE_FORMAT)),
        ]);
      });
    },
    getLoadFilteredEvents(
      startDate,
      endDate,
      finalQueryString,
      $page,
      searchTerm
    ) {
      const start = moment(startDate).format(DATE_FORMAT);
      const end = moment(endDate).format(DATE_FORMAT);
      getFilteredEvents(start, end, finalQueryString, 0, searchTerm).then(
        (eventlist) => {
          this.setEventList({
            eventlist: eventlist.data,
            total: eventlist.query.total,
          });
        }
      );
    },
    ...mapActions([
      "setEventList",
      "setClearAll",
      "setLoading",
      "setMonthEvents",
      "setCategoryList",
      "setFilter",
      "setSelectedMonth",
      "setDateManuallySelected",
    ]),
  },
  mounted() {
    const selectedMonth =
      this.$store.state.selectedMonth || moment().format("YYYY-MM");
    this.selectedMonth = selectedMonth;
    this.mobileMQ = window.matchMedia("(max-width: 768px)");
    this.isMobile = this.mobileMQ.matches;
    this.mobileMQ.addEventListener("change", this.resize);
  },
  beforeUnmount() {
    if (this.mobileMQ) {
      this.mobileMQ.removeEventListener("change", this.resize);
    }
  },
};
</script>
<style>
/* Empty style tag to ensure CSS is processed */
</style>
